import { useContext, useEffect, useState } from 'react';
import {
  AnsweringServiceType,
  AnsweringServiceBillingProductName,
} from '@bb/domain/dist/types/AnsweringSubscription';
import { getAnsweringServicePayerAndType } from 'api/AnsweringServiceController';
import { findActiveSubscription } from 'api/AnsweringSubscriptionController';
import type { ViewingLevel } from 'Router/pages/SubscriptionsPaymentsPage';
import type { AnsweringServicePayer } from '@/app-package';
import { AuthContext } from 'contexts';
import { message } from '@bb/elements-web';

interface UseAnsweringServiceDetailsParams {
  /** Whether we are viewing at the user level or org (brokerage) level */
  viewingLevel: ViewingLevel;
  /** The ID of the user viewing this page */
  userId?: string;
  /**
   * Whether the hook should actually fetch data. Defaults to true.
   * Pass false to skip the fetch entirely (e.g. when gated behind a feature flag
   * whose backend isn't ready to serve the request).
   *
   * TODO - Remove this flag when Stripe replacement is live (subscriptionHubFeature FF is no longer needed)
   */
  enabled?: boolean;
}

interface UseAnsweringServiceDetailsResult {
  /** What type of Answering Service this user/brokerage has (or is eligible to sign up for) */
  answeringServiceType: AnsweringServiceType;
  /** Who should pay for any Answering Service subscription in this brokerage */
  answeringServicePayer: AnsweringServicePayer;
  /** Whether the user has an active Answering Service subscription or is currently unsubscribed */
  hasActiveAnsweringService: boolean;
  /** Whether we encountered an error fetching the Answering Service details */
  fetchErrorAnsweringService: boolean;
  /** Whether the hook is still fetching the data */
  loadingAnsweringData: boolean;
}

/**
 * Shared hook to determine the Answering Service type, payer, and active status
 * for a given user or brokerage. Used by both the Subscriptions & Payments page
 * and the Answering Service page to avoid duplicating this fetch logic.
 */
const useAnsweringServiceDetails = ({
  viewingLevel,
  userId,
  enabled = true,
}: UseAnsweringServiceDetailsParams): UseAnsweringServiceDetailsResult => {
  const {
    answeringPortalConfig,
    auth: { org: userBrokerage },
  } = useContext(AuthContext);

  const [answeringServiceType, setAnsweringServiceType] =
    useState<AnsweringServiceType>();
  const [answeringServicePayer, setAnsweringServicePayer] =
    useState<AnsweringServicePayer>();
  const [hasActiveAnsweringService, setHasActiveAnsweringService] =
    useState<boolean>(false);
  const [fetchErrorAnsweringService, setFetchErrorAnsweringService] =
    useState<boolean>(false);
  // Start in non-loading state when disabled so callers don't wait on a fetch that will never fire
  const [loadingAnsweringData, setLoadingAnsweringData] =
    useState<boolean>(enabled);

  useEffect(() => {
    if (!enabled) {
      // Reset to a clean "not fetched" state so consumers get consistent values
      setAnsweringServiceType(undefined);
      setAnsweringServicePayer(undefined);
      setHasActiveAnsweringService(false);
      setLoadingAnsweringData(false);
      return;
    }

    const fetchAnsweringServiceDetails = async () => {
      setLoadingAnsweringData(true);
      try {
        const { payer, type } = await getAnsweringServicePayerAndType({
          orgId: userBrokerage.id,
        });
        setAnsweringServiceType(type);
        setAnsweringServicePayer(payer);

        if (
          type === AnsweringServiceType.Legacy ||
          (type === AnsweringServiceType.LiveConciergePlus &&
            payer === 'boardOrMls')
        ) {
          // We end here if there is no option available for the user/brokerage to upgrade to.
          // (i.e. it's legacy or the board/MLS already pays for LCS)
          setHasActiveAnsweringService(true);
        } else {
          // The burden is on a brokerage or user to pay an LCS subscription,
          // so check to see if they've signed up.
          const foundSubscription = await findActiveSubscription({
            subscriptionLevel: viewingLevel,
            userId,
          });

          if (foundSubscription) {
            setHasActiveAnsweringService(true);
            setAnsweringServiceType(foundSubscription.serviceType);
            if (
              foundSubscription.serviceType ===
              AnsweringServiceType.LiveConciergePlus
            ) {
              // If the found subscription was an LCS subscription,
              // at this point we know it is either a brokerage or user paid one
              // (board/MLS was filtered out above) so check the billingProductName.
              foundSubscription.billingProductName ===
              AnsweringServiceBillingProductName.BrokerageLevelLCS
                ? setAnsweringServicePayer('brokerage')
                : setAnsweringServicePayer('user');
            }
          } else {
            setHasActiveAnsweringService(false);
          }
        }
      } catch {
        setFetchErrorAnsweringService(true);
        message.error(
          'Error attempting to fetch Answering Service subscription details',
        );
      }
      setLoadingAnsweringData(false);
    };

    fetchAnsweringServiceDetails();
  }, [enabled, answeringPortalConfig, userBrokerage, userId, viewingLevel]);

  return {
    answeringServiceType,
    answeringServicePayer,
    hasActiveAnsweringService,
    fetchErrorAnsweringService,
    loadingAnsweringData,
  };
};

export default useAnsweringServiceDetails;
